
import { MapBoxService, Route } from "src/app/_services/mapbox.service";
import { MapRoute as AirRoute } from "src/app/_models/planning-assistant.interface"
import { PAUtil } from "./util";
import { PADataControl } from "../singletons/pa-data-control";
import { OperationService } from "../../../_services";
import { PACoordinates } from "./coordinates";

export class DistanceCalculator {
  constructor(
    private _mapBoxService: MapBoxService,
    private _operationService: OperationService
  ) {
  }

  realPositionDistances = new Map<string, { distance: number, duration: number }>()

  coordinatesToDistanceKey(from_lat: number, from_long: number, to_lat: number, to_long: number): string {
    return from_lat.toString() + "," + from_long.toString() + "," + to_lat.toString() + "," + to_long.toString()
  }

  routeLocalStorageKey(from_location: {coordinates: {latitude: number, longitude: number}}, to_location: {coordinates: {latitude: number, longitude: number}}): string {
    return `route:${from_location.coordinates.latitude},${from_location.coordinates.longitude};${to_location.coordinates.latitude},${to_location.coordinates.longitude}`
  }

  async getStreetDistanceFromLocationToCoordinates(location: DistanceCalculableLocation, lat: number, lon: number) {
    const distance_key = this.coordinatesToDistanceKey(location.coordinates.latitude, location.coordinates.longitude, lat, lon)
    if (!this.realPositionDistances.has(distance_key)) {
      await location.loadDistancesToCoordinates(lat, lon)
    }
    return this.realPositionDistances.get(distance_key)
  }

  async loadDistanceBetweenCoordinates(from_lat: number, from_lon: number, to_lat: number, to_lon: number): Promise<boolean> {
    const distance_key = this.coordinatesToDistanceKey(from_lat, from_lon, to_lat, to_lon)
    if (typeof localStorage[distance_key] != 'undefined') {
      let storage_string: String = localStorage[distance_key]
      let distance_string = storage_string.split(';')
      this.realPositionDistances.set(distance_key, {
        distance: Number.parseFloat(distance_string[0]),
        duration: Number.parseFloat(distance_string[1])
      })
    } else {
      return new Promise((resolve) => {
        this._operationService.getDistanceBetweenCoordinates(from_lat, from_lon, to_lat, to_lon).subscribe(
          (data) => {
            let operation_to_coordinates_key = this.coordinatesToDistanceKey(from_lat, from_lon, to_lat, to_lon)
            const distance = Math.round(data.distance)
            const duration = Math.round(data.duration)
            this.realPositionDistances.set(operation_to_coordinates_key, {distance: distance, duration: duration})
            localStorage[operation_to_coordinates_key] = distance.toString() + ';' + duration.toString()
            resolve(true)
          },
          (err) => {
            console.error(err)
            resolve(false)
          }
        )
      })
    }
  }

  async getRouteBetweenLocations(from_location: DistanceCalculableLocation, to_location: DistanceCalculableLocation): Promise<Route> {

    return new Promise(resolve => {
      this._mapBoxService.directionWaypoints(from_location.coordinates, to_location.coordinates).subscribe(
        (data) => {
          if (data.routes[0]) {
            const route: Route = {
              distance: Math.round(data.routes[0].distance / 1000),
              duration: Math.round(data.routes[0].duration / 60),
              geometry: {
                coordinates: data.routes[0].geometry.coordinates
              }
            }

            from_location.saveRouteToLocation(to_location, route, true);
            resolve(route)
          } else {
            resolve({distance: -1, duration: -1, geometry: {coordinates: []}})
          }
        },
        (err) => {
          console.log(err)
          resolve({distance: -1, duration: -1, geometry: {coordinates: []}})
        }
      )
    })
  }

  filterLocationsForUniqueCoordinates(locations: PALocation[]): PALocation[] {
    const unique_locations: PALocation[] = []
    for (let loc of locations) {
      if (!unique_locations.find(u_loc => u_loc.coordinates.longitude == loc.coordinates.longitude && u_loc.coordinates.latitude == loc.coordinates.latitude)) {
        unique_locations.push(loc)
      }
    }
    return unique_locations
  }

  async preloadLocationDistanceMatrix(source_locations: PALocation[], destination_locations: PALocation[], efficiency: 'min_credits' | 'min_time'): Promise<void> {
    source_locations = this.filterLocationsForUniqueCoordinates(source_locations)
    destination_locations = this.filterLocationsForUniqueCoordinates(destination_locations)
    const incomplete_source_locations = source_locations.filter(source_loc => !destination_locations.every(dest_loc => source_loc.distanceWasRequested(dest_loc)))
    const incomplete_destination_locations = destination_locations.filter(dest_loc => !source_locations.every(source_loc => source_loc.distanceWasRequested(dest_loc)))

    let requests: {matrix_locations: PALocation[], source_indices: number[], destination_indices: number[]}[] = []

    // gather requests
    if (efficiency == 'min_time') {
      const matrix_locations = [... new Set(incomplete_source_locations.concat(incomplete_destination_locations))]
      const source_indices = incomplete_source_locations.map(source_loc => matrix_locations.indexOf(source_loc))
      const destination_indices = incomplete_destination_locations.map(source_loc => matrix_locations.indexOf(source_loc))
      requests.push({matrix_locations: matrix_locations, source_indices: source_indices, destination_indices: destination_indices})
    } else {
      const partially_incomplete_source_locations = incomplete_source_locations.filter(source_loc => destination_locations.find(dest_loc => source_loc.distanceWasRequested(dest_loc)))
      for (let source_loc of partially_incomplete_source_locations) {
        const incomplete_source_destinations = incomplete_destination_locations.filter(dest_loc => !source_loc.distanceWasRequested(dest_loc))
        const matrix_locations = [... new Set([source_loc].concat(incomplete_source_destinations))]
        const source_indices = [matrix_locations.indexOf(source_loc)]
        const destination_indices = incomplete_source_destinations.map(dest_loc => matrix_locations.indexOf(dest_loc))
        requests.push({matrix_locations: matrix_locations, source_indices: source_indices, destination_indices: destination_indices})
      }
    }

    // send requests
    await Promise.all(requests.map(async request => {
      const matrix_coordinates = request.matrix_locations.map(loc => {
        return {latitude: loc.coordinates.latitude, longitude: loc.coordinates.longitude};
      })
      if (request.source_indices.length > 1 || request.destination_indices.length > 1) {
        return await new Promise<void>((resolve) => {
          this._mapBoxService.matrixWaypoints(matrix_coordinates, request.source_indices, request.destination_indices).subscribe(
            data => {
              // process answers
              for (let source_index of request.source_indices) {
                const source_location = request.matrix_locations[source_index];
                for (let destination_index of request.destination_indices) {
                  const destination_location = request.matrix_locations[destination_index];
                  let route: Route = {
                    distance: data.distances[request.source_indices.indexOf(source_index)][request.destination_indices.indexOf(destination_index)] / 1000,
                    duration: data.durations[request.source_indices.indexOf(source_index)][request.destination_indices.indexOf(destination_index)] / 60,
                    geometry: null
                  }
                  source_location.saveRouteToLocation(destination_location, route)
                }
              }
              resolve()
            },
            err => {
              console.log(err)
              resolve()
            }
          )
        })
      } else {
        return
      }
    }))
  }
}

export class DistanceCalculableLocation implements CalculatesDistance  {

  public distancesToLocations = new Map<DistanceCalculableLocation, Route>()
  public loadingRoutesToLocations: DistanceCalculableLocation[] = []

  constructor(
    public coordinates: {latitude: number, longitude: number},
    public distanceCalculator: DistanceCalculator
  ) {
  }

  public getDistanceToCoordinatesAsTheCrowFlies(lat: number, lon: number): number {
    return PAUtil.calcDistanceAsTheCrowFlies(this.coordinates.latitude, this.coordinates.longitude, lat, lon)
  }

  public async getStreetDistanceToCoordinates(lat: number, lon: number): Promise<{
    distance: number,
    duration: number
  }> {
    return await this.distanceCalculator.getStreetDistanceFromLocationToCoordinates(this, lat, lon)
  }

  public async getDistanceToLocation(to_location: DistanceCalculableLocation, ignore_loaded_geometry_check?: boolean): Promise<Route> {

    if (this == to_location || (to_location.coordinates.latitude == this.coordinates.latitude && to_location.coordinates.longitude == this.coordinates.longitude)) {
      return {distance: 0, duration: 0, geometry: {coordinates: []}}
    }
    let use_route: Route = null

    let local_storage_key = this.distanceCalculator.routeLocalStorageKey(this, to_location)
    let local_storage_value = localStorage.getItem(local_storage_key)
    let stored_route = (local_storage_value ? JSON.parse(local_storage_value) as Route : null)
    let session_route = this.distancesToLocations.get(to_location)
    if (stored_route && !session_route) {
      this.setDistanceToLocation(to_location, stored_route)
      use_route = stored_route
    } else if (session_route) {
      use_route = session_route
    }

    const invalid_route = !Number.isFinite(use_route?.distance) || !Number.isFinite(use_route?.duration)
    const route_has_loaded_geometry = use_route?.geometry

    return (use_route && !invalid_route && (ignore_loaded_geometry_check || route_has_loaded_geometry))? use_route : await this.loadDistanceToLocation(to_location)
  }

  public async loadDistanceToLocation(to_location: DistanceCalculableLocation): Promise<Route> {
    if (this.loadingRoutesToLocations.includes(to_location)) {

      await PAUtil.sleepUntilConditionBreaks(
        () => {
          return !this.distancesToLocations.has(to_location) && this.loadingRoutesToLocations.includes(to_location)
        },
        { console_warning_after_steps: [50, 1000] }
      )

      if (this.distancesToLocations.has(to_location)) {
        return this.distancesToLocations.get(to_location)
      } else {
        return {distance: -1, duration: -1, geometry: {coordinates: []}}
      }
    } else {
      this.loadingRoutesToLocations.push(to_location)
      let route = await this.distanceCalculator.getRouteBetweenLocations(this, to_location)
      PAUtil.removeElementFromList(this.loadingRoutesToLocations, to_location)
      return route
    }
  }

  public setDistanceToLocation(to_location: DistanceCalculableLocation, distance: Route) {
    this.distancesToLocations.set(to_location, distance)
  }

  public getDistanceToLocationAsTheCrowFlies(to_location: PALocation): number {
    return PAUtil.calcDistanceAsTheCrowFlies(this.coordinates.latitude, this.coordinates.longitude, to_location.coordinates.latitude, to_location.coordinates.longitude)
  }

  public distanceToLocationAsTheCrowFliesString(to_location: PALocation): string {
    let distance = this.getDistanceToLocationAsTheCrowFlies(to_location)
    return `${Math.round(distance)}km`
  }

  async loadDistancesToCoordinates(lat: number, long: number): Promise<boolean> {
    return await this.distanceCalculator.loadDistanceBetweenCoordinates(this.coordinates.latitude, this.coordinates.longitude, lat, long)
  }

  public saveRouteToLocation(to_location: DistanceCalculableLocation, route: Route, override_route?: boolean) {
    let local_storage_key = this.distanceCalculator.routeLocalStorageKey(this, to_location)
    if (override_route || !(this.distancesToLocations.has(to_location) || localStorage.getItem(local_storage_key))) {
      this.setDistanceToLocation(to_location, route)
      const route_string = JSON.stringify(route)
      PAUtil.setLocalStorageItem(local_storage_key, route_string)
    }
  }

  public getSyncStreetDistanceToLocation(location: PALocation): number {
    if (this.distancesToLocations.has(location)) {
      return this.distancesToLocations.get(location).distance
    } else {
      return -1
    }
  }

  public getSyncStreetDurationToLocation(location: PALocation, multiply_factor?: number): number {
    if (this.distancesToLocations.has(location)) {
      return Math.round(this.distancesToLocations.get(location).duration * (multiply_factor || 1))
    } else {
      return -1
    }
  }
}

export class PALocation extends DistanceCalculableLocation{

  constructor(
    override coordinates: PACoordinates,
    distanceCalculator: DistanceCalculator,
    public location_id: number,
    public zip_code: string,
    public street: string,
    public city: string
  ) {
    super(coordinates, distanceCalculator)
    PADataControl.Instance.locationMap.set(location_id, this)
  }

  public getAirDistanceRouteToLocation(location: PALocation, color: string): AirRoute {
    return {
      from: this,
      to: location,
      color: color,
      line_width: 4,
      dashed: true,
      popup_text: ''
    }
  }

  isInTourRadius(coordinates: number[][], radius: number, route_search_depth?: number): boolean {

    for (let coordinate of [coordinates[0], coordinates[coordinates.length - 1]]) {
      let distance = PAUtil.calcDistanceAsTheCrowFlies(coordinate[1], coordinate[0], this.coordinates.latitude, this.coordinates.longitude)
      if (distance <= radius) {
        return true
      }
    }

    // binary search like algorithm until specified depth
    let findCoordinates = (coordinates: number[][], depth?: number): boolean => {
      if (((Number.isInteger(depth) && depth) || !Number.isInteger(depth)) && coordinates.length) {
        let middle_idx = Math.ceil(coordinates.length / 2) - 1
        let middle_element = coordinates[middle_idx]
        if (PAUtil.calcDistanceAsTheCrowFlies(middle_element[1], middle_element[0], this.coordinates.latitude, this.coordinates.longitude) <= radius) {
          return true
        }
        let before_coordinates = coordinates.slice(0, middle_idx + 1)
        let after_coordinates = coordinates.slice(middle_idx + 2)

        return findCoordinates(before_coordinates, Number.isInteger(depth) ? depth - 1 : null) || findCoordinates(after_coordinates, Number.isInteger(depth) ? depth - 1 : null)
      } else {
        return false
      }
    }

    return findCoordinates(coordinates, route_search_depth)
  }

  distanceWasRequested(to_location: PALocation): boolean {
    let local_storage_key = this.distanceCalculator.routeLocalStorageKey(this, to_location)
    let local_storage_value = localStorage.getItem(local_storage_key)

    const same_coordinates = this.coordinates.latitude == to_location.coordinates.latitude && this.coordinates.longitude == to_location.coordinates.longitude

    return !!(this.distancesToLocations.has(to_location) || this.loadingRoutesToLocations.includes(to_location) || local_storage_value || same_coordinates)
  }
}

export declare interface CalculatesDistance {
  getDistanceToCoordinatesAsTheCrowFlies(lat: number, lon: number): number;

  getStreetDistanceToCoordinates(lat: number, lon: number): Promise<{ distance: number, duration: number }>

  getDistanceToLocation(to_location: DistanceCalculableLocation): Promise<{ distance: number; duration: number; }>
}
